import React, { useState, FC } from 'react';

import { Typography } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import cx from 'classnames';
import { useLocation, Link } from 'react-router-dom';

import ChevronTopIcon from '../../../../assets/icons/ChevronTopIcon';
import { colorsPalette } from '../../../../theme';
import { NavigationItem } from '../../../../types';
import { checkIfInternalLink } from '../../../../utils/helpers';
import { ExpandedNavigationItem } from '../../const';
import useStyles from './styles';

interface MenuItemProps {
  item: NavigationItem | ExpandedNavigationItem;
  isMobile: boolean;
  wrapperClass?: string;
  isManager?: boolean | null;
  handleClickLink?: () => void;
}

function isExpandedNavigationItem(
  pet: NavigationItem | ExpandedNavigationItem,
): pet is ExpandedNavigationItem {
  return (pet as ExpandedNavigationItem).subItems !== undefined;
}

const MenuItem: FC<MenuItemProps> = ({
  isManager,
  isMobile,
  item,
  wrapperClass,
  handleClickLink,
}) => {
  const classes = useStyles(
    isExpandedNavigationItem(item) && item?.subItems?.length && item?.subItems?.length > 4,
  )();

  const location = useLocation();
  const pathname = location?.pathname;

  const hasSubItems = isExpandedNavigationItem(item) && !!item?.subItems?.length;

  const managerLink = isExpandedNavigationItem(item) && item?.managerUrl;
  const link = isManager && managerLink ? managerLink : item?.url;
  const isInternalLink = checkIfInternalLink(link);
  const isActive = pathname === item?.url || pathname === managerLink;

  const [submenuOpened, setSubmenuOpened] = useState(false);

  const toggleSubmenu = () => {
    if (!isMobile) return;
    setSubmenuOpened((prevState) => !prevState);
  };

  return (
    <Box
      className={cx(
        classes.linkWrapper,
        hasSubItems && 'hasSubItems',
        wrapperClass,
        isExpandedNavigationItem(item) && item?.className ? item.className : '',
      )}
    >
      <div className={classes.linkWrap}>
        {isInternalLink ? (
          <Link
            to={link}
            className={cx(classes.link, isActive ? 'active' : '', hasSubItems && 'withSubItems')}
            onClick={() => handleClickLink && handleClickLink()}
          >
            {item?.title}
          </Link>
        ) : (
          <a
            href={link}
            className={cx(classes.link, isActive ? 'active' : '', hasSubItems && 'withSubItems')}
          >
            {item?.title}
          </a>
        )}
        {hasSubItems && (
          <ChevronTopIcon
            color={isMobile ? colorsPalette.blue : colorsPalette.white}
            className={cx(classes.linkIcon, submenuOpened && 'opened')}
            onClick={toggleSubmenu}
          />
        )}
      </div>
      {hasSubItems && (
        <ul className={cx(classes.sublistList, submenuOpened && 'opened')}>
          {isExpandedNavigationItem(item) &&
            item?.subItems &&
            item.subItems.map((subItem) => {
              const isInternalLink = checkIfInternalLink(subItem?.url);
              return (
                <li className={classes.sublistItem} key={subItem.title}>
                  {isInternalLink ? (
                    <Link
                      to={subItem.url}
                      className={classes.sublistLink}
                      onClick={() => handleClickLink && handleClickLink()}
                    >
                      <Typography className={classes.sublistTitle}>{subItem.title}</Typography>
                      <div className={classes.sublistDescription}>{subItem.description}</div>
                    </Link>
                  ) : (
                    <a href={subItem.url} className={classes.sublistLink}>
                      <Typography className={classes.sublistTitle}>{subItem.title}</Typography>
                      <div className={classes.sublistDescription}>{subItem.description}</div>
                    </a>
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </Box>
  );
};

export default MenuItem;
